/**
 * 性能监控工具 - Web Vitals 集成
 * 
 * 功能特性：
 * - 监控核心 Web Vitals 指标
 * - 自动上报性能数据
 * - 支持自定义性能标记
 */

import { logger } from './logger';

/**
 * 性能指标类型
 */
interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id?: string;
}

/**
 * 发送性能指标到监控服务
 */
const sendToAnalytics = (metric: PerformanceMetric): void => {
  // TODO: 集成实际的分析服务
  // 示例：Google Analytics, Sentry, 自定义后端等
  
  logger.performance(metric.name, metric.value);
  
  if (import.meta.env.PROD) {
    // 生产环境发送到监控服务
    // fetch('/api/analytics', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(metric),
    //   keepalive: true,
    // }).catch(() => {
    //   // 静默失败
    // });
  }
};

/**
 * 获取性能评级
 */
const getRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
  // Web Vitals 阈值
  const thresholds: Record<string, [number, number]> = {
    CLS: [0.1, 0.25],
    FID: [100, 300],
    FCP: [1800, 3000],
    LCP: [2500, 4000],
    TTFB: [800, 1800],
    INP: [200, 500],
  };

  const [good, poor] = thresholds[name] || [0, 0];
  
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
};

/**
 * 监控 Web Vitals
 * 
 * 注意：需要安装 web-vitals 包
 * npm install web-vitals
 * 
 * 注意：web-vitals v3+ 已移除 onFID，使用 onINP 替代
 */
export const initPerformanceMonitoring = (): void => {
  if (typeof window === 'undefined') return;

  // 使用动态导入以避免在不支持的环境中出错
  import('web-vitals')
    .then((webVitals) => {
      const { onCLS, onFCP, onLCP, onTTFB, onINP } = webVitals;
      
      // Cumulative Layout Shift (CLS)
      onCLS((metric: { value: number; delta: number; id: string }) => {
        sendToAnalytics({
          name: 'CLS',
          value: metric.value,
          rating: getRating('CLS', metric.value),
          delta: metric.delta,
          id: metric.id,
        });
      });

      // First Contentful Paint (FCP)
      onFCP((metric: { value: number; delta: number; id: string }) => {
        sendToAnalytics({
          name: 'FCP',
          value: metric.value,
          rating: getRating('FCP', metric.value),
          delta: metric.delta,
          id: metric.id,
        });
      });

      // Largest Contentful Paint (LCP)
      onLCP((metric: { value: number; delta: number; id: string }) => {
        sendToAnalytics({
          name: 'LCP',
          value: metric.value,
          rating: getRating('LCP', metric.value),
          delta: metric.delta,
          id: metric.id,
        });
      });

      // Time to First Byte (TTFB)
      onTTFB((metric: { value: number; delta: number; id: string }) => {
        sendToAnalytics({
          name: 'TTFB',
          value: metric.value,
          rating: getRating('TTFB', metric.value),
          delta: metric.delta,
          id: metric.id,
        });
      });

      // Interaction to Next Paint (INP) - replaces FID in web-vitals v3+
      onINP((metric: { value: number; delta: number; id: string }) => {
        sendToAnalytics({
          name: 'INP',
          value: metric.value,
          rating: getRating('INP', metric.value),
          delta: metric.delta,
          id: metric.id,
        });
      });

      logger.info('Performance monitoring initialized');
    })
    .catch((error) => {
      logger.warn('Failed to initialize performance monitoring', error);
    });
};

/**
 * 自定义性能标记
 */
export const performanceMark = (name: string): void => {
  if (typeof window === 'undefined' || !window.performance) return;
  
  try {
    window.performance.mark(name);
  } catch (error) {
    logger.warn(`Failed to create performance mark: ${name}`, error);
  }
};

/**
 * 测量性能
 */
export const performanceMeasure = (
  name: string,
  startMark: string,
  endMark?: string
): number | null => {
  if (typeof window === 'undefined' || !window.performance) return null;
  
  try {
    const measure = window.performance.measure(name, startMark, endMark);
    const duration = measure.duration;
    
    logger.performance(name, duration);
    
    return duration;
  } catch (error) {
    logger.warn(`Failed to measure performance: ${name}`, error);
    return null;
  }
};

/**
 * 清除性能标记
 */
export const clearPerformanceMarks = (): void => {
  if (typeof window === 'undefined' || !window.performance) return;
  
  try {
    window.performance.clearMarks();
    window.performance.clearMeasures();
  } catch (error) {
    logger.warn('Failed to clear performance marks', error);
  }
};

export default {
  init: initPerformanceMonitoring,
  mark: performanceMark,
  measure: performanceMeasure,
  clear: clearPerformanceMarks,
};
